import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Clock, MessageCircle, CheckCircle2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/lib/api';
import { SITE } from '@/lib/site';
import { TEST_IDS } from '@/constants/testIds/floreal';
import HoursList from '@/components/HoursList';
import { useSite } from '@/context/SiteContext';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errorMessage';

const EMPTY_FORM = { name: '', lastname: '', email: '', phone: '', message: '', privacy: false, website: '' };

const MENU_LINKS = [
    { to: '/', label: 'Home' },
    { to: '/atelier', label: "L'atelier" },
    { to: '/matrimoni', label: 'Matrimoni' },
    { to: '/workshop', label: 'Workshop' },
    { to: '/giardini', label: 'Giardini' },
    { to: '/galleria', label: 'Galleria' },
    { to: '/eventi', label: 'Eventi' },
];

function ContactForm() {
    const { t } = useSite();
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = 'Necessario';
        if (!form.lastname.trim()) e.lastname = 'Necessario';
        if (!form.email.trim()) e.email = 'Necessario';
        else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'Email non valida';
        // Telefono ora obbligatorio
        if (!form.phone.trim()) e.phone = 'Necessario';
        else if (!/^[+\d\s\-()]{6,}$/.test(form.phone.trim())) e.phone = 'Numero non valido';
        if (!form.message.trim() || form.message.trim().length < 5) e.message = 'Scrivi un messaggio di almeno 5 caratteri';
        if (!form.privacy) e.privacy = 'Necessario per inviare la richiesta';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev?.preventDefault?.();
        if (submitting) return;              // no double submit
        if (!validate()) return;
        setSubmitting(true);
        try {
            await api.post('/contact', {
                name: `${form.name.trim()} ${form.lastname.trim()}`,
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                message: form.message.trim(),
                website: form.website || '',   // honeypot
            });
            setSuccess(true);
            setForm(EMPTY_FORM);               // clear form on success
        } catch (err) {
            toast.error(errorMessage(err, 'Qualcosa non ha funzionato. Riprova tra poco.'));
        } finally { setSubmitting(false); }
    };

    if (success) {
        return (
            <motion.div
                data-testid="contact-success-message"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="rounded-xl bg-atelier-sage/15 border border-atelier-sage/30 p-8 text-center"
            >
                <CheckCircle2 className="h-8 w-8 mx-auto text-atelier-sage-ink" />
                <h3 className="mt-3 font-serif text-2xl">{t('contatti.success.title', 'Grazie, abbiamo ricevuto la tua richiesta.')}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t('contatti.success.description', 'Ti risponderemo il prima possibile via email.')}</p>
                <Button variant="outline" onClick={() => setSuccess(false)} className="mt-5" data-testid="contact-form-reset">
                    Invia un altro messaggio
                </Button>
            </motion.div>
        );
    }

    return (
        <form id="form" data-testid={TEST_IDS.contactForm} onSubmit={onSubmit} className="rounded-xl border border-border bg-card p-6 sm:p-8 space-y-4 scroll-mt-24" noValidate>
            {/* Honeypot — hidden field for spam bots */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                <label htmlFor="c-website">Il tuo sito web (non compilare)</label>
                <input
                    id="c-website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
            </div>

            <div>
                <p className="eyebrow mb-1">{t('contatti.form.eyebrow', 'Richiedi informazioni')}</p>
                <h3 className="font-serif text-2xl">{t('contatti.form.title', 'Compila il form e ti risponderemo il prima possibile.')}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="c-name" className="eyebrow">Nome *</Label>
                    <Input id="c-name" data-testid={TEST_IDS.contactFormName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cn('mt-1.5', errors.name && 'border-destructive')} />
                    {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
                </div>
                <div>
                    <Label htmlFor="c-lastname" className="eyebrow">Cognome *</Label>
                    <Input id="c-lastname" data-testid="contact-form-lastname" value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} className={cn('mt-1.5', errors.lastname && 'border-destructive')} />
                    {errors.lastname && <p className="text-xs text-destructive mt-1">{errors.lastname}</p>}
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="c-email" className="eyebrow">Email *</Label>
                    <Input id="c-email" data-testid={TEST_IDS.contactFormEmail} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cn('mt-1.5', errors.email && 'border-destructive')} />
                    {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                </div>
                <div>
                    <Label htmlFor="c-phone" className="eyebrow">Telefono *</Label>
                    <Input id="c-phone" data-testid={TEST_IDS.contactFormPhone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={cn('mt-1.5', errors.phone && 'border-destructive')} placeholder="Es. 339 123 4567" />
                    {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
                </div>
            </div>
            <div>
                <Label htmlFor="c-message" className="eyebrow">Messaggio *</Label>
                <Textarea id="c-message" data-testid={TEST_IDS.contactFormMessage} rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={cn('mt-1.5', errors.message && 'border-destructive')} placeholder="Raccontaci come possiamo aiutarti…" />
                {errors.message && <p className="text-xs text-destructive mt-1">{errors.message}</p>}
            </div>
            <label className="flex items-start gap-3 cursor-pointer pt-1">
                <Checkbox
                    data-testid={TEST_IDS.contactFormPrivacy}
                    checked={form.privacy}
                    onCheckedChange={(v) => setForm({ ...form, privacy: !!v })}
                    className="mt-0.5"
                />
                <span className="text-sm text-muted-foreground leading-relaxed">
                    Acconsento al trattamento dei miei dati personali per essere ricontattato/a, come descritto nell'<Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">informativa privacy</Link>. *
                </span>
            </label>
            {errors.privacy && <p className="text-xs text-destructive -mt-2">{errors.privacy}</p>}
            <Button type="submit" data-testid={TEST_IDS.contactFormSubmit} disabled={submitting || !form.privacy} className="h-12 w-full sm:w-auto px-6">
                {submitting ? 'Un momento…' : (<><Send className="h-4 w-4 mr-2" />Invia</>)}
            </Button>
        </form>
    );
}

export default function Contatti() {
    const { t } = useSite();
    const location = useLocation();
    useSeo({
        title: `${SITE_NAME} — Contatti`,
        description: 'Floreal Atelier del Fiore — Via Unione 3, Lomazzo (CO). Telefono, WhatsApp, email e indicazioni stradali. Aperti martedì–sabato.',
    });

    // If we arrive with hash "#form", smooth-scroll the ContactForm into view.
    useEffect(() => {
        if (location.hash !== '#form') return;
        // Wait a tick so the DOM is fully rendered
        const t = setTimeout(() => {
            const el = document.getElementById('form');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 120);
        return () => clearTimeout(t);
    }, [location.hash]);

    return (
        <>
            <section className="pt-32 sm:pt-40 pb-6">
                <div className="container-atelier max-w-3xl">
                    <p className="eyebrow mb-4">{t('contatti.hero.eyebrow', 'Contatti')}</p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        {t('contatti.hero.title', 'Vieni a trovarci in atelier.')}
                    </h1>
                </div>
            </section>

            <Section className="pt-6 sm:pt-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                    {/* Info — Primo riquadro + Secondo riquadro (menu) */}
                    <div className="space-y-6">
                        {/* PRIMO RIQUADRO */}
                        <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="rounded-xl border border-border bg-card p-7">
                            <div className="flex items-start gap-4">
                                <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center shrink-0">
                                    <MapPin className="h-5 w-5 text-atelier-bronze-soft" strokeWidth={1.5} />
                                </div>
                                <div className="flex-1">
                                    <p className="eyebrow mb-2">L&apos;atelier</p>
                                    <p className="font-serif text-xl">Floreal — Atelier del fiore</p>
                                    <p className="text-sm text-muted-foreground mt-1">{SITE.addressLines.join(' — ')}</p>
                                    <p className="text-sm text-muted-foreground mt-2">Consegne a domicilio.</p>
                                    <a href={SITE.googleMapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm mt-3 underline underline-offset-4 decoration-border hover:decoration-foreground transition-colors">
                                        Apri su Google Maps
                                    </a>
                                </div>
                            </div>
                        </motion.div>

                        {/* Orari */}
                        <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.06 }} className="rounded-xl border border-border bg-card p-7">
                            <div className="flex items-start gap-4">
                                <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center shrink-0">
                                    <Clock className="h-5 w-5 text-atelier-bronze-soft" strokeWidth={1.5} />
                                </div>
                                <div className="flex-1">
                                    <p className="eyebrow mb-3">Orari</p>
                                    <div data-testid="contatti-hours">
                                        <HoursList />
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Telefono ed email */}
                        <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.12 }} className="rounded-xl border border-border bg-card p-7">
                            <p className="eyebrow mb-4">Telefono ed email</p>
                            <ul className="space-y-3">
                                {SITE.phones.map((p) => (
                                    <li key={p.tel}>
                                        <a data-testid={TEST_IDS.contactPhoneLink} href={`tel:${p.tel}`} className="group inline-flex items-center gap-3">
                                            <Phone className="h-4 w-4 text-atelier-bronze-soft" />
                                            <span className="text-foreground group-hover:text-atelier-sage-ink transition-colors">{p.value}</span>
                                            <span className="text-xs text-muted-foreground">{p.label}</span>
                                        </a>
                                    </li>
                                ))}
                                <li>
                                    <a data-testid={TEST_IDS.contactEmailLink} href={`mailto:${SITE.email}`} className="group inline-flex items-center gap-3">
                                        <Mail className="h-4 w-4 text-atelier-bronze-soft" />
                                        <span className="text-foreground group-hover:text-atelier-sage-ink transition-colors">{SITE.email}</span>
                                    </a>
                                </li>
                            </ul>
                        </motion.div>

                        <div className="flex flex-wrap gap-3">
                            <Button asChild className="h-12 px-5" data-testid={TEST_IDS.contactMapsCta}>
                                <a href={SITE.googleMapsUrl} target="_blank" rel="noreferrer">
                                    <MapPin className="h-4 w-4 mr-2" /> Vieni a trovarci
                                </a>
                            </Button>
                            <Button asChild variant="outline" className="h-12 px-5 border-foreground/20 bg-background">
                                <a data-testid={TEST_IDS.contactWhatsappCta} href={SITE.whatsappUrl} target="_blank" rel="noreferrer">
                                    <MessageCircle className="h-4 w-4 mr-2" /> Scrivici su WhatsApp
                                </a>
                            </Button>
                            <Button asChild variant="ghost" className="h-12 px-5">
                                <a data-testid={TEST_IDS.contactInstagramLink} href={SITE.instagramUrl} target="_blank" rel="noreferrer">
                                    <Instagram className="h-4 w-4 mr-2" /> Instagram
                                </a>
                            </Button>
                        </div>
                    </div>

                    {/* Map + form */}
                    <div className="space-y-6">
                        <motion.div initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                            <div className="rounded-xl border border-border overflow-hidden bg-secondary">
                                <iframe
                                    data-testid={TEST_IDS.contactMap}
                                    src={SITE.googleMapsEmbedSrc}
                                    title="Mappa di Floreal Atelier del Fiore a Lomazzo"
                                    width="100%"
                                    height="360"
                                    className="block w-full aspect-[16/10]"
                                    style={{ border: 0 }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </motion.div>
                        <ContactForm />
                    </div>
                </div>
            </Section>
        </>
    );
}
